import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { BarChart2, PieChart, TrendingUp, Users, Award, Download } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import TeamPerformance from '../components/analytics/TeamPerformance';
import GoalDistribution from '../components/analytics/GoalDistribution';
import PlayerStats from '../components/analytics/PlayerStats';

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { tournament, teams, matches, exportData } = useAppContext();
  const [completedMatches, setCompletedMatches] = useState([]);
  const [activeTab, setActiveTab] = useState('team');
  
  // Redirect if no tournament
  useEffect(() => {
    if (!tournament) {
      navigate('/');
    }
  }, [tournament, navigate]);
  
  // Filter completed matches
  useEffect(() => {
    setCompletedMatches(matches.filter(match => match.status === 'completed'));
  }, [matches]);
  
  if (!tournament) return null;
  
  // Check if we have enough data for analytics
  const hasEnoughData = completedMatches.length > 0;
  
  // Handle data export
  const handleExport = () => {
    const data = exportData();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tournament.name.replace(/\s+/g, '_')}_data.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  return (
    <div className="px-4 py-6 max-w-xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center">
          <BarChart2 size={24} className="mr-2" />
          Analytics
        </h1>
        
        <Button
          tertiary
          small
          icon={<Download size={16} />}
          onClick={handleExport}
        >
          Export Data
        </Button>
      </div>
      
      {!hasEnoughData ? (
        <Card className="p-8 text-center">
          <div className="mb-4 text-gray-500 dark:text-gray-400">
            <BarChart2 size={48} className="mx-auto mb-2" />
            <p className="text-lg font-medium">Not Enough Data</p>
          </div>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Complete some matches to see analytics and insights.
          </p>
          <Button
            primary
            onClick={() => navigate('/schedule')}
          >
            Go to Schedule
          </Button>
        </Card>
      ) : (
        <>
          {/* Summary Card */}
          <Card className="mb-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-500 mb-1">
                  {completedMatches.length}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Matches Played</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-green-500 mb-1">
                  {completedMatches.reduce((total, match) => total + match.homeScore + match.awayScore, 0)}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Total Goals</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-500 mb-1">
                  {(completedMatches.reduce((total, match) => total + match.homeScore + match.awayScore, 0) / completedMatches.length).toFixed(1)}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Goals per Match</div>
              </div>
            </div>
          </Card>
          
          {/* Tabs */}
          <div className="flex space-x-1 mb-4 border-b dark:border-gray-700">
            <button
              className={`py-2 px-4 text-sm font-medium ${
                activeTab === 'team' 
                  ? 'border-b-2 border-blue-500 text-blue-500' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
              onClick={() => setActiveTab('team')}
            >
              <span className="flex items-center">
                <Users size={16} className="mr-1" />
                Team Performance
              </span>
            </button>
            
            <button
              className={`py-2 px-4 text-sm font-medium ${
                activeTab === 'goals' 
                  ? 'border-b-2 border-blue-500 text-blue-500' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
              onClick={() => setActiveTab('goals')}
            >
              <span className="flex items-center">
                <PieChart size={16} className="mr-1" />
                Goal Stats
              </span>
            </button>
            
            <button
              className={`py-2 px-4 text-sm font-medium ${
                activeTab === 'players' 
                  ? 'border-b-2 border-blue-500 text-blue-500' 
                  : 'text-gray-500 dark:text-gray-400'
              }`}
              onClick={() => setActiveTab('players')}
            >
              <span className="flex items-center">
                <Award size={16} className="mr-1" />
                Player Stats
              </span>
            </button>
          </div>
          
          {/* Content based on active tab */}
          {activeTab === 'team' && (
            <TeamPerformance teams={teams} matches={completedMatches} />
          )}
          
          {activeTab === 'goals' && (
            <GoalDistribution teams={teams} matches={completedMatches} />
          )}
          
          {activeTab === 'players' && (
            <PlayerStats teams={teams} matches={completedMatches} />
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
